import {
  Inventory,
  InventoryCategory,
  InventoryTransfer,
  RequestBasePostInventory,
  RequestBasePostInventoryCategory,
  RequestBasePostInventoryTransfer,
  RequestBasePostInventoryTransferAdminReturn,
  RequestBasePutInventoryCategoryNavigation,
  ResponseBaseGetInventories,
  ResponseBaseGetInventoryCategories,
  ResponseBaseGetInventoryCategoriesNavigation,
  ResponseBasePostInventory,
} from '@hefe/models';
import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';
import { InventorySlice } from './inventory.slice';

export const inventoryApi = createApi({
  reducerPath: 'apiInventory',
  baseQuery: API.baseQuery,
  tagTypes: [
    'InventoryCategory',
    'InventoryCategories',
    'InventoryCategoriesNavigation',
    'Inventory',
    'InventoryTag',
    'InventoriesOfCategory',
    'UserInventory',
  ],
  endpoints: (builder) => ({
    /**************************************************************************
     *                              INVENTORY
     *************************************************************************/
    fetchAllInventories: builder.query<ResponseBaseGetInventories, number>({
      query: (id) => `/inventory-categories/${id ?? 0}/inventories`,
      providesTags: (result, error, id) => [
        { type: 'InventoriesOfCategory', id: id ?? 0 },
      ],
    }),
    fetchInventory: builder.query<Inventory, number>({
      query: (id) => `/inventories/${id}`,
      providesTags: (result, error, id) => [
        { type: 'Inventory', id: result?.id },
      ],
    }),
    fetchUserInventories: builder.query<Inventory[], void>({
      query: (id) => `/user/inventories`,
      providesTags: (result) => [{ type: 'UserInventory', id: undefined }],
    }),
    saveInventory: builder.mutation<
      ResponseBasePostInventory,
      {
        request: RequestBasePostInventory;
        oldCategoryId?: number;
      }
    >({
      query: (request) => ({
        url: `/inventories`,
        method: 'POST',
        body: request.request,
      }),
      invalidatesTags: (result) => [{ type: 'Inventory', id: result?.id }],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          // new category
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchAllInventories',
              responseData.category_id ?? 0,
              (draft) => {
                const inventoryIndex = draft?.findIndex(
                  (inventory) => inventory.id === responseData.id
                );

                if (inventoryIndex !== -1) {
                  draft[inventoryIndex] = responseData;
                } else {
                  draft?.push(responseData);
                }
              }
            )
          );
          // old category
          if (request?.oldCategoryId !== responseData?.category_id) {
            dispatch(
              inventoryApi.util.updateQueryData(
                'fetchAllInventories',
                request?.oldCategoryId ?? 0,
                (draft) =>
                  draft.filter((inventory) => inventory.id !== responseData?.id)
              )
            );
          }
        } catch (error: unknown) {}
      },
    }),
    deleteInventory: builder.mutation<void, Inventory>({
      query: (inventory) => ({
        url: `/inventories/${inventory.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result) => [{ type: 'UserInventory', id: undefined }],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchAllInventories',
              request.category_id ?? 0,
              (draft) =>
                draft.filter((inventory) => inventory.id !== request.id)
            )
          );
        } catch (error: unknown) {}
      },
    }),
    /**************************************************************************
     *                              TRANSFERS
     *************************************************************************/
    fetchShareLinkDetails: builder.query<InventoryTransfer, string>({
      query: (transferId) => `/inventories/share-link/${transferId}`,
    }),
    createTransfer: builder.mutation<
      Inventory,
      RequestBasePostInventoryTransfer
    >({
      query: (request) => ({
        url: `/inventories/${request.inventory_id}/transfers`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result) => [
        {
          type: 'InventoriesOfCategory',
          id: result?.category_id ?? 0,
        },
      ],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchInventory',
              responseData.id,
              (draft) => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    createAdminReturnRequest: builder.mutation<
      Inventory,
      RequestBasePostInventoryTransferAdminReturn
    >({
      query: (request) => ({
        url: `/inventories/${request.inventory_id}/transfers/request-return`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result) => [
        {
          type: 'InventoriesOfCategory',
          id: result?.category_id ?? 0,
        },
      ],
      // Add inventory transfer as first item in the transfer of the inventory
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchInventory',
              responseData.id,
              (draft) => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    createShareLink: builder.mutation<Inventory, Inventory>({
      query: (inventory) => ({
        url: `/inventories/${inventory.id}/transfers/create-share-link`,
        method: 'POST',
      }),
      invalidatesTags: (result) => [
        { type: 'UserInventory', id: undefined },
        {
          type: 'InventoriesOfCategory',
          id: result?.category_id ?? 0,
        },
      ],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchInventory',
              responseData.id,
              (draft) => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    deleteTransfer: builder.mutation<Inventory, InventoryTransfer>({
      query: (transfer) => ({
        url: `/inventories/${transfer.inventory_id}/transfers/${transfer.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, kek, transfer) => {
        return [
          { type: 'UserInventory', id: undefined },
          {
            type: 'InventoriesOfCategory',
            id: transfer?.inventory?.category_id ?? 0,
          },
        ];
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchInventory',
              request.inventory_id,
              (draft) => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    confirmTransfer: builder.mutation<Inventory, Inventory>({
      query: (inventory) => ({
        url: `/inventories/${inventory.id}/transfers/confirm`,
        method: 'POST',
      }),
      invalidatesTags: (result) => [
        { type: 'UserInventory', id: undefined },
        {
          type: 'InventoriesOfCategory',
          id: result?.category_id ?? 0,
        },
      ],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchInventory',
              responseData.id,
              (draft) => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    confirmReturn: builder.mutation<Inventory, InventoryTransfer>({
      query: (transfer) => ({
        url: `/inventories/${transfer.inventory_id}/transfers/confirm-return`,
        method: 'POST',
      }),
      invalidatesTags: (result) => [
        {
          type: 'InventoriesOfCategory',
          id: result?.category_id ?? 0,
        },
      ],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchInventory',
              responseData.id,
              (draft) => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    confirmShareLinkTransfer: builder.mutation<
      Inventory,
      { inventoryId: number; shareTransferId: string }
    >({
      query: (request) => ({
        url: `/inventories/${request.inventoryId}/transfers/confirm-share-link/${request.shareTransferId}`,
        method: 'POST',
      }),
      invalidatesTags: (result) => [
        { type: 'UserInventory', id: undefined },
        {
          type: 'InventoriesOfCategory',
          id: result?.category_id ?? 0,
        },
      ],
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchInventory',
              responseData.id,
              (draft) => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    /**************************************************************************
     *                              CATEGORIES
     *************************************************************************/
    fetchNavigation: builder.query<
      ResponseBaseGetInventoryCategoriesNavigation,
      void
    >({
      query: (request) => '/inventory-categories/navigation',
      providesTags: ['InventoryCategoriesNavigation'],
    }),
    fetchCategories: builder.query<ResponseBaseGetInventoryCategories, void>({
      query: () => '/inventory-categories',
      providesTags: ['InventoryCategories'],
    }),
    fetchCategory: builder.query<InventoryCategory, number | undefined>({
      query: (request) => `/inventory-categories/${request}`,
      providesTags: (result, error, id) => [
        { type: 'InventoryCategory', id: result?.id },
      ],
    }),
    addCategory: builder.mutation<
      ResponseBaseGetInventoryCategoriesNavigation,
      RequestBasePostInventoryCategory
    >({
      query: (request) => ({
        url: `/inventory-categories`,
        method: 'POST',
        body: request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchNavigation',
              undefined,
              (draft) => responseData
            )
          );

          dispatch(InventorySlice.actions.setSelectedKey(`${request.id}`));
        } catch (error: unknown) {}
      },
    }),
    updateCategoryNavigation: builder.mutation<
      ResponseBaseGetInventoryCategoriesNavigation,
      RequestBasePutInventoryCategoryNavigation
    >({
      query: (request) => ({
        url: `/inventory-categories/navigation`,
        method: 'PUT',
        body: request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchNavigation',
              undefined,
              () => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    deleteCategory: builder.mutation<
      ResponseBaseGetInventoryCategories,
      number
    >({
      query: (id) => ({
        url: `/inventory-categories/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            inventoryApi.util.updateQueryData(
              'fetchNavigation',
              undefined,
              () => responseData
            )
          );

          dispatch(InventorySlice.actions.setSelectedKey('uncategorized'));
        } catch (error: unknown) {}
      },
    }),
  }), // end of endpoints
});

// Export hooks for usage in functional components
export const {
  // Inventory
  useFetchAllInventoriesQuery,
  useFetchInventoryQuery,
  useFetchUserInventoriesQuery,
  useSaveInventoryMutation,
  useDeleteInventoryMutation,
  // Inventory Transfers
  useLazyFetchShareLinkDetailsQuery,
  useCreateTransferMutation,
  useCreateAdminReturnRequestMutation,
  useCreateShareLinkMutation,
  useConfirmTransferMutation,
  useConfirmReturnMutation,
  useConfirmShareLinkTransferMutation,
  useDeleteTransferMutation,
  // Inventory Categories
  useFetchNavigationQuery,
  useFetchCategoriesQuery,
  useFetchCategoryQuery,
  useAddCategoryMutation,
  useUpdateCategoryNavigationMutation,
  useDeleteCategoryMutation,
  reducerPath,
  reducer,
  middleware,
} = inventoryApi;
